"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Thread } from "@/types/chat";
type ThreadItemProps = {
  thread: Thread;
  isSelected: boolean;
  isMultiSelected: boolean;
  isInContext?: boolean;
  onSelect: () => void;
  onMultiSelect: (e: React.MouseEvent) => void;
  onDelete: () => Promise<void>;
  onUpdateTitle: (newTitle: string) => Promise<void>;
  onAddToContext?: () => void;
  onShowInfo?: () => void;
  showShareToExchange?: boolean;
  depth: number;
};
export function ThreadItem(t0) {
  const $ = _c(74);
  const {
    thread,
    isSelected,
    isMultiSelected,
    isInContext: t1,
    onSelect,
    onMultiSelect,
    onDelete,
    onUpdateTitle,
    onAddToContext,
    onShowInfo,
    showShareToExchange: t2,
    depth
  } = t0;
  const isInContext = t1 === undefined ? false : t1;
  const showShareToExchange = t2 === undefined ? true : t2;
  const router = useRouter();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(thread.title || "New thread");
  const editInputRef = useRef(null);
  let t3;
  if ($[0] !== thread) {
    t3 = {
      type: "thread",
      thread
    };
    $[0] = thread;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3 || $[3] !== thread.id) {
    t4 = {
      id: thread.id,
      data: t3
    };
    $[2] = t3;
    $[3] = thread.id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t4);
  let t5;
  if ($[5] !== transform) {
    t5 = CSS.Transform.toString(transform);
    $[5] = transform;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = isDragging ? 0.5 : 1;
  let t7;
  if ($[7] !== t5 || $[8] !== t6 || $[9] !== transition) {
    t7 = {
      transform: t5,
      transition,
      opacity: t6
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = transition;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const style = t7;
  let t8;
  let t9;
  if ($[11] !== isEditing) {
    t8 = () => {
      if (isEditing && editInputRef.current) {
        editInputRef.current.focus();
        editInputRef.current.select();
      }
    };
    t9 = [isEditing];
    $[11] = isEditing;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== thread.title) {
    t10 = e => {
      e.stopPropagation();
      setEditedTitle(thread.title || "New thread");
      setIsEditing(true);
    };
    $[14] = thread.title;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleStartEdit = t10;
  let t11;
  if ($[16] !== editedTitle || $[17] !== onUpdateTitle || $[18] !== thread.title) {
    t11 = async () => {
      const trimmedTitle = editedTitle.trim();
      if (trimmedTitle && trimmedTitle !== thread.title) {
        await onUpdateTitle(trimmedTitle);
      }
      setIsEditing(false);
    };
    $[16] = editedTitle;
    $[17] = onUpdateTitle;
    $[18] = thread.title;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleSaveEdit = t11;
  let t12;
  if ($[20] !== handleSaveEdit || $[21] !== thread.title) {
    t12 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        handleSaveEdit();
      } else {
        if (e_0.key === "Escape") {
          setIsEditing(false);
          setEditedTitle(thread.title || "New thread");
        }
      }
    };
    $[20] = handleSaveEdit;
    $[21] = thread.title;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const handleKeyDown = t12;
  let t13;
  if ($[23] !== onDelete || $[24] !== thread.title) {
    t13 = async e_1 => {
      e_1.stopPropagation();
      const confirmed = window.confirm(`Are you sure you want to delete "${thread.title || "Untitled"}.thread"?\n\nThis will permanently delete the .thread file and all its messages.`);
      if (confirmed) {
        await onDelete();
      }
    };
    $[23] = onDelete;
    $[24] = thread.title;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleDelete = t13;
  const paddingLeft = depth * 12 + 24;
  let t14;
  if ($[26] !== onMultiSelect || $[27] !== onSelect) {
    t14 = e_2 => {
      if (e_2.ctrlKey || e_2.metaKey || e_2.shiftKey) {
        onMultiSelect(e_2);
      } else {
        onSelect();
      }
    };
    $[26] = onMultiSelect;
    $[27] = onSelect;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const handleClick = t14;
  let t15;
  if ($[29] !== onAddToContext) {
    t15 = e_3 => {
      e_3.stopPropagation();
      if (onAddToContext) {
        onAddToContext();
      }
    };
    $[29] = onAddToContext;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const handleAddToContext = t15;
  let t16;
  if ($[31] !== onShowInfo) {
    t16 = e_4 => {
      e_4.stopPropagation();
      if (onShowInfo) {
        onShowInfo();
      }
    };
    $[31] = onShowInfo;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const handleShowInfo = t16;
  let t17;
  if ($[33] !== router || $[34] !== thread.id) {
    t17 = e_5 => {
      e_5.stopPropagation();
      router.push(`/exchange?share=thread&threadId=${thread.id}`);
    };
    $[33] = router;
    $[34] = thread.id;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const handleShareToExchange = t17;
  let t18;
  if ($[36] !== isInContext || $[37] !== isMultiSelected || $[38] !== isSelected) {
    t18 = () => {
      if (isInContext) {
        return "bg-lavender/30 text-foreground ring-1 ring-lavender/50";
      }
      if (isMultiSelected) {
        return "bg-sky/30 text-foreground ring-1 ring-sky/50";
      }
      if (isSelected) {
        return "bg-white/60 text-foreground font-medium";
      }
      return "text-foreground hover:bg-white/40";
    };
    $[36] = isInContext;
    $[37] = isMultiSelected;
    $[38] = isSelected;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const getSelectionClasses = t18;
  const t19 = `group relative flex items-center rounded-md px-2 py-1.5 cursor-grab active:cursor-grabbing transition-colors ${getSelectionClasses()}`;
  let t20;
  if ($[40] !== paddingLeft) {
    t20 = {
      paddingLeft
    };
    $[40] = paddingLeft;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== isMultiSelected) {
    t21 = isMultiSelected ? <svg className="h-4 w-4 flex-shrink-0 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg> : <svg className="h-4 w-4 flex-shrink-0 mr-2 text-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>;
    $[42] = isMultiSelected;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== editedTitle || $[45] !== handleKeyDown || $[46] !== handleSaveEdit || $[47] !== isEditing || $[48] !== thread.title) {
    t22 = isEditing ? <div className="flex-1 flex items-center gap-1"><input ref={editInputRef} type="text" value={editedTitle} onChange={e_6 => setEditedTitle(e_6.target.value)} onKeyDown={handleKeyDown} onBlur={handleSaveEdit} onClick={_temp} className="flex-1 rounded border border-white/40 bg-white/60 px-1.5 py-0.5 text-sm text-foreground focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky" /><span className="text-xs text-foreground/50">.thread</span></div> : <span className="flex-1 truncate text-sm">{thread.title || "Untitled"}<span className="text-foreground/50">.thread</span></span>;
    $[44] = editedTitle;
    $[45] = handleKeyDown;
    $[46] = handleSaveEdit;
    $[47] = isEditing;
    $[48] = thread.title;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== handleAddToContext || $[51] !== handleDelete || $[52] !== handleShareToExchange || $[53] !== handleShowInfo || $[54] !== handleStartEdit || $[55] !== isEditing || $[56] !== isInContext || $[57] !== onAddToContext || $[58] !== onShowInfo || $[59] !== showShareToExchange) {
    t23 = !isEditing && <div className="absolute right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">{onAddToContext && <button type="button" onClick={handleAddToContext} className={`rounded p-1 hover:bg-white/40 ${isInContext ? "text-lavender hover:text-lavender" : "text-foreground/40 hover:text-lavender"}`} title={isInContext ? "Already in context" : "Add to context"}><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg></button>}{onShowInfo && <button type="button" onClick={handleShowInfo} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-sky" title="Thread properties"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg></button>}{showShareToExchange && <button type="button" onClick={handleShareToExchange} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-emerald-500" title="Share to Exchange"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" /></svg></button>}<button type="button" onClick={handleStartEdit} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-sky" title="Rename thread"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg></button><button type="button" onClick={handleDelete} className="rounded p-1 hover:bg-white/40 text-foreground/40 hover:text-red-400" title="Delete thread"><svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg></button></div>;
    $[50] = handleAddToContext;
    $[51] = handleDelete;
    $[52] = handleShareToExchange;
    $[53] = handleShowInfo;
    $[54] = handleStartEdit;
    $[55] = isEditing;
    $[56] = isInContext;
    $[57] = onAddToContext;
    $[58] = onShowInfo;
    $[59] = showShareToExchange;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== attributes || $[62] !== handleClick || $[63] !== listeners || $[64] !== t19 || $[65] !== t20 || $[66] !== t21 || $[67] !== t22 || $[68] !== t23) {
    t24 = <div {...attributes} {...listeners} onClick={handleClick} className={t19} style={t20}>{t21}{t22}{t23}</div>;
    $[61] = attributes;
    $[62] = handleClick;
    $[63] = listeners;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== setNodeRef || $[71] !== style || $[72] !== t24) {
    t25 = <div ref={setNodeRef} style={style}>{t24}</div>;
    $[70] = setNodeRef;
    $[71] = style;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  return t25;
}
function _temp(e_7) {
  return e_7.stopPropagation();
}